/**
 * Reads a dropped archive in the browser and says what the server will make of
 * it. Most of the server's checks are mirrored here, not all of them: the 1 GB
 * limit is on a whole upload, which spans every file in the drop, so only the
 * server can apply it. The server stays the source of truth either way.
 */
import { unzipSync } from "fflate";

import { MAX_FOLDER_FILES } from "./uploadKeys";

/** Largest archive the server accepts, and the largest we will read in the browser. */
export const MAX_ZIP_BYTES = 200 * 1024 * 1024;

/**
 * Largest archive this reader opens. Reading one holds the whole file in memory for
 * a check the server makes again, so past this size the server does it on its own.
 */
export const PREVIEW_MAX_BYTES = 50 * 1024 * 1024;

/** Largest total the server lets an archive expand to once it is extracted. */
export const MAX_UNCOMPRESSED_BYTES = 1024 * 1024 * 1024;

/** Largest single member the server extracts out of an archive. */
export const MAX_MEMBER_BYTES = 128 * 1024 * 1024;

/** Largest number of shapefiles the server accepts in one archive. */
export const MAX_SHAPEFILES_PER_ZIP = 25;

/** Largest number of members the server accepts in one archive. */
export const MAX_ZIP_ENTRIES = 1000;

/**
 * Below this total the server leaves the expansion ratio alone: a small archive of
 * nothing but .dbf can reach 100:1 on its own, because .dbf is space padded.
 */
export const RATIO_FLOOR_BYTES = 128 * 1024 * 1024;

/** Expansion the server treats as a bomb, once the total is past the floor. */
export const MAX_COMPRESSION_RATIO = 100;

/**
 * What fflate reports for a member whose real size sits in a zip64 extra field it
 * does not read. Such a member holds at least 4 GB, which is over every limit here,
 * so the verdict stands and only the number is left out of the line.
 */
const ZIP64_UNKNOWN_SIZE = 0xffffffff;

/** Longest member name we print, the length the server prints as well. */
const MAX_NAME_LENGTH = 80;

/** Most shapefile names one line lists before it ends with "and N more". */
const MAX_NAMES_LISTED = 8;

/** Most groups a problem sentence lists before it ends with "and N more". */
const MAX_GROUPS_LISTED = 3;

/** Extensions the server keeps when it extracts an archive. */
const ALLOWED_EXTENSIONS = [".shp", ".shx", ".dbf", ".prj", ".cpg"];

/** Order the missing extensions are listed in, the order the server names them. */
const REQUIRED_EXTENSIONS = [".shp", ".dbf", ".shx"];

/**
 * Thrown out of the member filter and caught in previewZip. fflate calls the filter
 * once per member the central directory declares, so a truncated file that claims
 * billions of members has to be stopped inside the walk rather than after it.
 */
const TOO_MANY_ENTRIES = new Error("zip declares more members than the server accepts");

export interface ZipEntry {
  name: string;
  /** Uncompressed size, read from the central directory. Nothing is inflated to get it. */
  size: number;
  /** Compressed size, from the same record. The pair gives the expansion ratio. */
  compressedSize: number;
}

export interface ZipPreview {
  /**
   * Which of the two things this describes. Several of the sentences below read
   * differently for a folder, so what it is has to be said rather than guessed at
   * from a count that happens to be set.
   */
  kind: "folder" | "archive";
  /** Display names of the groups that hold .shp, .shx and .dbf. */
  shapefiles: string[];
  /** Groups that are missing a required member, and the extensions they lack. */
  skipped: { name: string; missing: string[] }[];
  /** Complete groups that carry no .prj, so the projection has to be guessed. */
  missingProjection?: string[];
  /** Total group count, set only when it is over the server's limit. */
  tooMany?: number;
  /** Members the archive declares, left unset when the walk stopped early. */
  entryCount?: number;
  /** This reader could not open the file. The server reads it again and decides. */
  unreadable?: boolean;
  /** The file is over the server's archive limit, so it was never read. */
  tooLarge?: boolean;
  /** The file is too large to read here, so only the server looks inside it. */
  notChecked?: boolean;
  /** Size of the file itself, set only when it is over the archive limit. */
  zipBytes?: number;
  /** The archive declares more members than the server reads. */
  tooManyEntries?: boolean;
  /** The archive's members expand past the server's uncompressed limit. */
  tooBigUncompressed?: boolean;
  /** What the members expand to, set only when that total is over the limit. */
  uncompressedBytes?: number;
  /** The archive expands out of proportion to its compressed size. */
  ratioTooHigh?: boolean;
  /** The first member that is on its own larger than the server extracts. */
  memberTooLarge?: { name: string; size?: number };
  /** The first path that two members share once the paths are cleaned. */
  duplicateName?: string;
  /** The first path that still climbs out of the upload folder once it is cleaned. */
  unsafePath?: string;
  /** The .tif and .tiff files a dropped folder holds. Only a folder counts them. */
  geoTiffs?: number;
  /** Subfolders that were passed over, because only one level of a folder is read. */
  ignoredSubfolders?: number;
  /** Files in a folder whose extension the upload does not take. */
  ignoredFiles?: number;
  /** The folder holds more files than one level of reading takes, so it was not read. */
  tooManyFolderFiles?: boolean;
}

/** True for a folder's description, false for an archive's. */
function isFolder(preview: ZipPreview): boolean {
  return preview.kind === "folder";
}

export interface GroupOptions {
  /**
   * Turned off for a dropped folder. Four of the limits below are the server's rules
   * for an archive and nothing else: how many members one zip's central directory may
   * declare, how many shapefiles one zip may hold, how far one zip may expand, and how
   * large one member it unpacks may be. A folder reaches the server as loose files,
   * which none of the four touches, and the 500 MB limit on the whole upload already
   * covers it.
   */
  archiveLimits?: boolean;
}

/** Writes a limit the way it is written in the product: "200 MB", "1 GB". */
function formatLimit(bytes: number): string {
  const megabytes = bytes / (1024 * 1024);
  return megabytes >= 1024
    ? `${Math.round(megabytes / 1024)} GB`
    : `${Math.round(megabytes)} MB`;
}

/**
 * Writes a size that was measured. Small sizes keep a decimal and larger ones round
 * up, so a size that is only just over a limit never prints as the limit itself.
 */
function formatSize(bytes: number): string {
  const megabytes = bytes / (1024 * 1024);
  const value = megabytes >= 1024 ? megabytes / 1024 : megabytes;
  const unit = megabytes >= 1024 ? "GB" : "MB";
  return value < 10 ? `${value.toFixed(1)} ${unit}` : `${Math.ceil(value)} ${unit}`;
}

export const MAX_ZIP_LABEL = formatLimit(MAX_ZIP_BYTES);

export const MAX_MEMBER_LABEL = formatLimit(MAX_MEMBER_BYTES);

export const MAX_UNCOMPRESSED_LABEL = formatLimit(MAX_UNCOMPRESSED_BYTES);

function extensionOf(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot === -1 ? "" : name.slice(dot).toLowerCase();
}

function isMacOsMetadata(segments: string[]): boolean {
  return segments.some((segment) => segment === "__MACOSX" || segment.startsWith("._"));
}

/**
 * Lists an archive's members from its central directory. The filter never returns
 * true, so fflate walks the directory and inflates nothing, and it stops the walk one
 * member past the server's limit, which is far enough to name a count that is only
 * just over it.
 */
export function listZipEntries(data: Uint8Array): ZipEntry[] {
  const entries: ZipEntry[] = [];
  unzipSync(data, {
    filter: (file) => {
      if (entries.length > MAX_ZIP_ENTRIES) throw TOO_MANY_ENTRIES;
      entries.push({
        name: file.name,
        size: file.originalSize,
        compressedSize: file.size
      });
      return false;
    }
  });
  return entries;
}

/**
 * Writes a list as prose: "a", "a and b", "a, b and c". Past the limit the tail
 * becomes a count, so one archive never fills the row with names.
 */
function listSentence(items: string[], limit = Number.POSITIVE_INFINITY): string {
  if (items.length === 0) return "";
  if (items.length === 1) return items[0];
  if (items.length > limit) {
    return `${items.slice(0, limit).join(", ")} and ${items.length - limit} more`;
  }
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

/**
 * The path the server extracts a member to: the separators are made uniform, empty
 * and "." segments drop out, and ".." takes the segment before it with it. A ".."
 * that is left at the front had nothing to resolve against, which is the path that
 * climbs out of the upload folder.
 */
function sanitisePath(name: string): string {
  const cleaned: string[] = [];
  for (const segment of name.replace(/\\/g, "/").split("/")) {
    if (segment === "" || segment === ".") continue;
    const previous = cleaned[cleaned.length - 1];
    if (segment === ".." && cleaned.length > 0 && previous !== "..") {
      cleaned.pop();
      continue;
    }
    cleaned.push(segment);
  }
  return cleaned.join("/");
}

/** A Windows drive letter at the front of a name, as in "C:" or "C:Users". */
const DRIVE_PREFIX = /^[A-Za-z]:/;

/**
 * Names the server turns down before it cleans them, because cleaning would hide
 * what is wrong with them: a path from the root of the disk, a path from a Windows
 * drive, and a name that carries a NUL byte, which cuts the name short in C.
 */
function isUnsafeName(name: string): boolean {
  if (name.includes("\0")) return true;
  const uniform = name.replace(/\\/g, "/");
  return uniform.startsWith("/") || DRIVE_PREFIX.test(uniform.split("/")[0]);
}

/** A cleaned path that still climbs out of the folder the upload is written to. */
function isUnsafePath(path: string): boolean {
  return path === ".." || path.startsWith("../");
}

/**
 * The shapefile one file belongs to: its name without the extension. Nothing comes
 * back for a file the server never groups, which is an extension it does not keep,
 * and a name that is nothing but an extension and so has no base name to group
 * under. The drop reads a folder with this as well, so a shapefile is one shapefile
 * wherever it is read.
 */
export function shapefileStem(name: string): string | undefined {
  const extension = extensionOf(name);
  if (!ALLOWED_EXTENSIONS.includes(extension)) return undefined;
  return name.slice(0, name.length - extension.length) || undefined;
}

/**
 * Groups members by directory plus base name, the way the server groups them, so
 * the preview does not promise a shapefile the server then refuses. archiveBytes is
 * the size of the file on disk, which is what the expansion ratio is measured
 * against; without it the ratio falls back to the compressed sizes of the members.
 */
export function groupZipEntries(
  entries: ZipEntry[],
  archiveBytes?: number,
  { archiveLimits = true }: GroupOptions = {}
): ZipPreview {
  const groups = new Map<string, Set<string>>();
  const keptPaths = new Set<string>();
  let uncompressedBytes = 0;
  let compressedBytes = 0;
  let memberTooLarge: { name: string; size?: number } | undefined;
  let sizeUnknown = false;
  let duplicateName: string | undefined;
  let unsafePath: string | undefined;

  for (const entry of entries) {
    if (entry.name.endsWith("/")) continue;

    const path = sanitisePath(entry.name);
    if (!unsafePath) {
      // the name as it was written is what is wrong, so that is what is reported
      if (isUnsafeName(entry.name)) unsafePath = entry.name;
      else if (isUnsafePath(path)) unsafePath = path;
    }

    const segments = path.split("/");
    if (isMacOsMetadata(segments)) continue;

    const base = segments[segments.length - 1];
    const extension = extensionOf(base);
    if (!ALLOWED_EXTENSIONS.includes(extension)) continue;

    // Only the members the server extracts count against the size limits. It drops
    // macOS metadata and every other extension first, so a huge .txt beside a small
    // shapefile adds nothing to the total. A member of an unknown size is left out
    // of the totals and answered on its own
    if (entry.size === ZIP64_UNKNOWN_SIZE) {
      sizeUnknown = true;
      memberTooLarge = memberTooLarge ?? { name: path };
    } else {
      uncompressedBytes += entry.size;
      compressedBytes += entry.compressedSize;
      if (!memberTooLarge && entry.size > MAX_MEMBER_BYTES) {
        memberTooLarge = { name: path, size: entry.size };
      }
    }

    // Two members can reach the same path once it is cleaned, and the second one then
    // quietly replaces the first on disk. The extension is matched without its case,
    // because the server writes it out in lower case
    const keptPath = `${path.slice(0, path.length - extension.length)}${extension}`;
    if (!duplicateName && keptPaths.has(keptPath)) {
      duplicateName = path;
    }
    keptPaths.add(keptPath);

    // A member named only ".shp" has no base name to group under, and the server
    // passes over it rather than build a group with an empty name
    const stem = shapefileStem(base);
    if (!stem) continue;

    const directory = segments.slice(0, -1).join("/");
    const key = directory ? `${directory}/${stem}` : stem;

    const members = groups.get(key) ?? new Set<string>();
    members.add(extension);
    groups.set(key, members);
  }

  // A base name used in two directories is shown with its directory, so two
  // different shapefiles never read as one repeated name
  const stemCounts = new Map<string, number>();
  for (const key of groups.keys()) {
    const stem = key.split("/").pop();
    stemCounts.set(stem, (stemCounts.get(stem) ?? 0) + 1);
  }
  const displayNameOf = (key: string): string => {
    const stem = key.split("/").pop();
    return stemCounts.get(stem) > 1 ? key : stem;
  };

  const shapefiles: string[] = [];
  const skipped: { name: string; missing: string[] }[] = [];
  const missingProjection: string[] = [];

  for (const [key, members] of groups) {
    const name = displayNameOf(key);
    const missing = REQUIRED_EXTENSIONS.filter((extension) => !members.has(extension));
    if (missing.length > 0) {
      skipped.push({ name, missing });
      continue;
    }
    shapefiles.push(name);
    // A complete group still imports without its .prj, on a guessed projection
    if (!members.has(".prj")) {
      missingProjection.push(name);
    }
  }

  const preview: ZipPreview = {
    kind: "archive",
    shapefiles,
    skipped,
    entryCount: entries.length
  };
  if (missingProjection.length > 0) {
    preview.missingProjection = missingProjection;
  }
  if (archiveLimits && entries.length > MAX_ZIP_ENTRIES) {
    preview.tooManyEntries = true;
  }
  if (archiveLimits && groups.size > MAX_SHAPEFILES_PER_ZIP) {
    preview.tooMany = groups.size;
  }
  if (archiveLimits && (sizeUnknown || uncompressedBytes > MAX_UNCOMPRESSED_BYTES)) {
    preview.tooBigUncompressed = true;
    // the total is short of a member that was never counted, so it is not printed
    if (!sizeUnknown) {
      preview.uncompressedBytes = uncompressedBytes;
    }
  }
  if (archiveLimits && memberTooLarge) {
    preview.memberTooLarge = memberTooLarge;
  }
  if (duplicateName) {
    preview.duplicateName = duplicateName;
  }
  if (unsafePath) {
    preview.unsafePath = unsafePath;
  }
  // Both halves of the server's test: a large total on its own is a legitimate export,
  // and a high ratio on its own is a small archive of padded .dbf. The divisor is the
  // size of the archive on disk, the same number the server divides by, so a member it
  // never extracts still counts towards how compressed the file as a whole is
  const ratioDivisor = archiveBytes ?? compressedBytes;
  if (
    uncompressedBytes > RATIO_FLOOR_BYTES &&
    uncompressedBytes > MAX_COMPRESSION_RATIO * ratioDivisor
  ) {
    preview.ratioTooHigh = true;
  }
  return preview;
}

/**
 * Describes one dropped archive. Never throws. What comes back decides whether the
 * file is sent: a structural refusal the server makes the same way, such as an
 * unsafe path or a count over a limit, takes the file out of the drop, and anything
 * else is still sent. A file this reader cannot open is one of those, because it
 * and the server's reader do not accept exactly the same archives.
 */
export async function previewZip(file: File): Promise<ZipPreview> {
  if (file.size > MAX_ZIP_BYTES) {
    return {
      kind: "archive",
      shapefiles: [],
      skipped: [],
      tooLarge: true,
      zipBytes: file.size
    };
  }
  if (file.size > PREVIEW_MAX_BYTES) {
    return { kind: "archive", shapefiles: [], skipped: [], notChecked: true };
  }

  try {
    const data = new Uint8Array(await file.arrayBuffer());
    return groupZipEntries(listZipEntries(data), file.size);
  } catch (error) {
    if (error === TOO_MANY_ENTRIES) {
      return { kind: "archive", shapefiles: [], skipped: [], tooManyEntries: true };
    }
    return { kind: "archive", shapefiles: [], skipped: [], unreadable: true };
  }
}

/**
 * Control characters come out before the length check, the way the server prints an
 * archive-supplied name: a member name can carry newlines or terminal escapes.
 */
export function shorten(name: string): string {
  const clean = name.replace(/\p{C}/gu, "");
  return clean.length > MAX_NAME_LENGTH
    ? `${clean.slice(0, MAX_NAME_LENGTH - 1)}…`
    : clean;
}

/**
 * A shapefile name as it reads inside a sentence. The quotes hold it apart from the
 * folder and archive names it sits beside, which are never quoted, and they go on
 * after the name is shortened so a cut name keeps its closing quote.
 */
export function quoteName(name: string): string {
  return `"${shorten(name)}"`;
}

/**
 * quoted is off for the line that lists what a drop imports, which reads as an
 * inventory rather than as prose, and quoting eight names there is only noise.
 */
function namesSentence(names: string[], quoted = false): string {
  const shown = names
    .slice(0, MAX_NAMES_LISTED)
    .map((name) => (quoted ? quoteName(name) : shorten(name)));
  if (names.length <= MAX_NAMES_LISTED) return shown.join(", ");
  return `${shown.join(", ")} and ${names.length - MAX_NAMES_LISTED} more`;
}

/** " (2 GB)" when the size is known, nothing when it is not. */
function sizeNote(bytes?: number): string {
  return bytes === undefined ? "" : ` (${formatSize(bytes)})`;
}

/** Said under an archive that was too large to read here. */
const NOT_CHECKED_HEAD = "Checked on upload";

/** Said about an archive the browser could not open. */
const UNREADABLE_NOTE = "Could not be read in the browser, the server will check it";

/** How the row under the file name should read. */
export type ZipStatusTone = "ok" | "warn" | "bad";

export interface ZipStatus {
  /** Sentence case, no trailing period: the head and the note in one sentence. */
  text: string;
  /** "bad" when nothing in the archive imports, "warn" when something is left behind. */
  tone: ZipStatusTone;
  /** What the archive does import. Empty when it imports nothing. */
  head: string;
  /** What is left behind, or why the archive is turned down. Empty when neither. */
  note: string;
}

/**
 * The one thing that stops the archive importing, or the empty string when nothing
 * does. The order is the server's: the checks it makes first are named first.
 */
function problemText(preview: ZipPreview): string {
  if (preview.tooLarge) {
    return `Too large${sizeNote(preview.zipBytes)}, the limit is ${MAX_ZIP_LABEL}`;
  }
  if (preview.unsafePath) {
    return `Unsafe file path (${shorten(preview.unsafePath)})`;
  }
  if (preview.tooManyEntries) {
    const count = preview.entryCount ?? `more than ${MAX_ZIP_ENTRIES}`;
    return `Too many files (${count}), the limit is ${MAX_ZIP_ENTRIES}`;
  }
  // The reader stopped as soon as it knew the folder was over the limit, so it can
  // say how many the limit is and not how many the folder holds
  if (preview.tooManyFolderFiles) {
    return `Too many files, the limit is ${MAX_FOLDER_FILES} files in one folder`;
  }
  if (preview.tooBigUncompressed) {
    return `Too large when unpacked${sizeNote(
      preview.uncompressedBytes
    )}, the limit is ${MAX_UNCOMPRESSED_LABEL}`;
  }
  if (preview.ratioTooHigh) {
    return `Too large when unpacked, more than ${MAX_COMPRESSION_RATIO} times the zip size`;
  }
  if (preview.tooMany) {
    return `Too many shapefiles (${preview.tooMany}), the limit is ${MAX_SHAPEFILES_PER_ZIP}`;
  }
  if (preview.memberTooLarge) {
    const { name, size } = preview.memberTooLarge;
    const measured = size === undefined ? "" : `, ${formatSize(size)}`;
    return `File too large (${shorten(
      name
    )}${measured}), the limit is ${MAX_MEMBER_LABEL}`;
  }
  if (preview.duplicateName) {
    return `Duplicate file name (${shorten(preview.duplicateName)})`;
  }

  // Only complete groups import, so an archive of loose parts is a problem as well
  if (preview.shapefiles.length > 0) return "";
  // A folder can carry GeoTIFFs and no shapefile at all, and those still import
  if (preview.geoTiffs > 0) return "";

  if (preview.skipped.length > 0) {
    const groups = preview.skipped.map(
      // a group can lack three files at most, so that list is never cut
      (group) => `${quoteName(group.name)} is missing ${listSentence(group.missing)}`
    );
    const label =
      preview.skipped.length === 1 ? "Incomplete shapefile" : "Incomplete shapefiles";
    return `${label}, ${listSentence(groups, MAX_GROUPS_LISTED)}`;
  }

  if (isFolder(preview)) {
    // A parent folder that holds the shapefile one level down is the drop we turn
    // down most often, and calling it empty tells the user nothing they can act on
    if (preview.ignoredSubfolders) {
      return "The files are in a subfolder. The upload reads only the files directly in the folder";
    }
    // A folder whose files were all passed over is not empty either: it holds
    // nothing we take
    return preview.entryCount === 0 && !preview.ignoredFiles
      ? "The folder is empty"
      : "No shapefiles or GeoTIFFs inside";
  }
  if (preview.entryCount === 0) return "The zip file is empty";
  return "No shapefiles inside";
}

/** What is worth adding to the list of what the drop does import. */
function noteText(preview: ZipPreview): string {
  const ignored = ignoredText(preview);
  const left = leftBehindText(preview);
  if (!left) return ignored;
  return ignored ? `${left}. ${ignored}` : left;
}

/**
 * What one level of reading passed over. Only a folder has anything to say here: a
 * zip is read whole, and its own members are answered for by the lines above.
 */
function ignoredText(preview: ZipPreview): string {
  const parts: string[] = [];
  const subfolders = preview.ignoredSubfolders ?? 0;
  const files = preview.ignoredFiles ?? 0;
  if (subfolders > 0) {
    parts.push(
      `${subfolders} ${subfolders === 1 ? "subfolder was" : "subfolders were"} ignored`
    );
  }
  if (files > 0) {
    parts.push(`${files} other ${files === 1 ? "file was" : "files were"} ignored`);
  }
  if (parts.length === 0) return "";
  return `${listSentence(parts)}, only the files in the folder are read`;
}

/** What the drop leaves behind, or has to guess at. */
function leftBehindText(preview: ZipPreview): string {
  if (preview.skipped.length === 1) {
    const only = preview.skipped[0];
    return `${quoteName(only.name)} is missing ${listSentence(
      only.missing
    )} and will be skipped`;
  }
  if (preview.skipped.length > 1) {
    const groups = preview.skipped.map(
      (group) => `${quoteName(group.name)} (${group.missing.join(", ")})`
    );
    return `${listSentence(
      groups,
      MAX_GROUPS_LISTED
    )} are missing files and will be skipped`;
  }

  const projection = preview.missingProjection ?? [];
  if (projection.length === 0) return "";
  const names = listSentence(
    projection.map((name) => quoteName(name)),
    MAX_GROUPS_LISTED
  );
  return `${names} ${
    projection.length === 1 ? "is" : "are"
  } missing .prj. Defaulting CRS to EPSG:4326 (WGS84)`;
}

/**
 * What the drop imports. An archive and a folder both name their shapefiles, because
 * nothing else says what is inside them. A folder counts its GeoTIFFs as well, so one
 * line covers both halves of what it holds.
 */
function headText(preview: ZipPreview): string {
  const count = preview.shapefiles.length;
  const shapefiles =
    count > 0
      ? `${count} ${count === 1 ? "shapefile" : "shapefiles"}: ${namesSentence(
          preview.shapefiles
        )}`
      : "";
  if (!isFolder(preview)) return shapefiles;

  const tiffs = preview.geoTiffs;
  if (!tiffs) return shapefiles;
  const geoTiffs = `${tiffs} ${tiffs === 1 ? "GeoTIFF" : "GeoTIFFs"}`;
  // A count joined to the names with "and" reads as one more name, so the GeoTIFFs
  // are counted in a sentence of their own
  return shapefiles ? `${shapefiles}. ${geoTiffs}` : geoTiffs;
}

/**
 * Turns a preview into the line shown under the file name, and its colour. The two
 * halves come back on their own as well, because the list shows what the archive
 * imports and the issues panel shows what it leaves behind.
 */
export function zipStatus(preview: ZipPreview): ZipStatus {
  // Nothing was read, so there is nothing to warn about: the line under the name only
  // says when the archive is looked at
  if (preview.notChecked) {
    return {
      text: NOT_CHECKED_HEAD,
      tone: "ok",
      head: NOT_CHECKED_HEAD,
      note: ""
    };
  }

  // The reader in the browser and the one on the server do not accept exactly the
  // same archives, so a file this one cannot open is still sent and the server says
  if (preview.unreadable) {
    return {
      text: UNREADABLE_NOTE,
      tone: "warn",
      head: "",
      note: UNREADABLE_NOTE
    };
  }

  const problem = problemText(preview);
  if (problem) {
    return { text: problem, tone: "bad", head: "", note: problem };
  }

  const head = headText(preview);
  const note = noteText(preview);

  // Either a skipped group or an estimated projection is worth saying, and both are
  // written by the note, so the note is what decides the colour
  return {
    text: note ? `${head}. ${note}` : head,
    tone: note ? "warn" : "ok",
    head,
    note
  };
}

/**
 * Names what the archive still imports, to follow a note about what it leaves
 * behind. One complete shapefile is named on its own, and several are counted
 * first, so the sentence does not open with a list.
 */
export function stillUploadedSentence(preview: ZipPreview): string {
  const names = preview.shapefiles;
  if (names.length === 0) return "";
  if (names.length === 1) return `${quoteName(names[0])} will still be uploaded`;
  return `${names.length} shapefiles will still be uploaded: ${namesSentence(
    names,
    true
  )}`;
}
